// Supabase API module
// Configuration from environment variables
import { getDeviceId } from '../../device-id';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// Validate environment variables
if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.error('[API] Missing required environment variables: VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
}

// Device ID for notification sender exclusion + realtime self-broadcast filter.
// Re-exported from the canonical helper so realtime.ts (which imports it from
// here) and usePushNotifications share the SAME persistent id that travels as
// sender_device_id (triple-review 2026-06-11 C8).
export { getDeviceId };

// Errors
export class UnauthorizedError extends Error {
  constructor(message = 'Unauthorized') {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

// Unauthorized handler — injected at boot from main.tsx. Replaces the
// pre-Phase-1a `window.dispatchEvent('shopman:unauthorized')` broadcast.
// The Phase 1a service-layer invariant bans CustomEvent for app-events,
// and the allow-zone treats this file as the producer; the consumer (ListPage)
// registers via `container.auth?.onUnauthorized(handler)`.
type UnauthorizedHandler = (listName: string) => void;
let _unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  _unauthorizedHandler = fn;
}

// Phase A: user-token provider — injected at boot from main.tsx.
// When set, request() uses the user Bearer token for Authorization header
// instead of the anon key. The anon key stays in the `apikey` header (required
// for Supabase routing). This is orthogonal to list-action tokens which travel
// in the body (`token` field) or `X-Access-Token` header.
// Async-capable: the Supabase impl awaits getSession() to refresh an expired
// access token before returning it (native/Capacitor background-resume case).
type UserTokenProvider = () => string | null | Promise<string | null>;
let _userTokenProvider: UserTokenProvider | null = null;

export function setUserTokenProvider(fn: UserTokenProvider | null): void {
  _userTokenProvider = fn;
}

// Types
export interface ListData {
  id: string;
  name: string;
  slug: string;
  name_lowercase: string;
  owner_user_id?: string | null;
  list_access_password_hash: string | null;
  list_admin_password_hash: string | null;
  view_requires_password: boolean;
  edit_requires_password: boolean;
  has_access_password: boolean;
  has_admin_password: boolean;
  created_at: string;
  shopping_country?: string | null; // ISO 3166-1 country code for translation target
  list_language?: string; // ISO 639-1 language code for list items (default: "unknown")
}

export interface ItemData {
  id: string;
  list_id: string;
  name: string;
  amount: string | null;
  type: string | null;
  note: string | null;
  is_bought: boolean;
  created_at: string;
  translated_name?: string | null; // Cached translated name
  detected_lang?: string; // Language detected during AI parsing
}

// Supported languages for list language setting
export const SUPPORTED_LANGUAGES = {
  unknown: 'Auto-detect',
  da: 'Dansk',
  en: 'English',
  de: 'Deutsch',
  sv: 'Svenska',
  no: 'Norsk',
  fi: 'Suomi',
  nl: 'Nederlands',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  pt: 'Português',
  pl: 'Polski',
  cs: 'Čeština',
  hu: 'Magyar',
  el: 'Ελληνικά',
  tr: 'Türkçe',
} as const;

export type LanguageCode = keyof typeof SUPPORTED_LANGUAGES;

export interface CreateListParams {
  name: string;
  password?: string | null;
  viewRequiresPassword?: boolean;
  editRequiresPassword?: boolean;
}

export interface ArchiveData {
  id: string;
  list_id: string;
  archived_at: string;
  items: ItemData[];
}

export interface UndoData {
  action: string;
  items?: ItemData[];
  archive?: ArchiveData;
}

// AI parsing types
// Phase A: My Lists response shape
export interface MyListItem {
  list_id: string;
  list_name: string;
  slug: string;
  role: string;
  member_count: number;
  unbought_count: number;
}

export interface ParsedItem {
  name: string;
  amount: string | null;
  type: string | null;
  note: string | null;
  confidence: number;
  detected_lang: string;
}

export interface ParseTextResponse {
  items: ParsedItem[];
  provider_used: string;
  tokens_used: number | null;
  remaining_today: number;
  suggested_language?: string; // Most common detected language across items
}

export interface ParseTextError {
  error: string;
  message: string;
  resets_at?: string;
  can_retry?: boolean;
  alternate_provider?: string;
}

export interface BatchAddItem {
  name: string;
  amount?: string | null;
  type?: string | null;
  note?: string | null;
  confidence?: number;
  detected_lang?: string;
}

export interface BatchAddResponse {
  added: Array<{ temp_id: string; id: string; name: string }>;
  failed: Array<{ temp_id: string; name: string; error: string }>;
}

// Translation types
export interface TranslationItem {
  id: string;
  name: string;
  detected_lang?: string;
}

export interface TranslationResult {
  id: string;
  original: string;
  translated: string;
  confidence: number;
  cached: boolean;
}

export interface TranslateResponse {
  translations: TranslationResult[];
  target_lang: string;
  remaining_today: number;
}

// Country codes for shopping destinations (European focus)
export const SUPPORTED_COUNTRIES = {
  // Nordic
  DK: 'Danmark',
  NO: 'Norge',
  SE: 'Sverige',
  FI: 'Finland',
  // Central/Western Europe
  DE: 'Tyskland',
  AT: 'Østrig',
  CH: 'Schweiz',
  NL: 'Holland',
  BE: 'Belgien',
  FR: 'Frankrig',
  IT: 'Italien',
  ES: 'Spanien',
  PT: 'Portugal',
  // UK/Ireland
  GB: 'Storbritannien',
  IE: 'Irland',
  // Eastern Europe
  PL: 'Polen',
  CZ: 'Tjekkiet',
  HU: 'Ungarn',
  HR: 'Kroatien',
  // Greek/Turkish
  GR: 'Grækenland',
  TR: 'Tyrkiet',
} as const;

export type CountryCode = keyof typeof SUPPORTED_COUNTRIES;

// API client
class ApiClient {
  private baseUrl: string;
  private anonKey: string;

  constructor() {
    this.baseUrl = SUPABASE_URL;
    this.anonKey = SUPABASE_ANON_KEY;
  }

  // Get the base URL for connectivity checks
  getBaseUrl(): string {
    return this.baseUrl;
  }

  // Helper for making requests
  // Phase A: User token is only sent for edge function calls (/functions/v1/).
  // PostgREST endpoints (/rest/v1/) MUST use the anon key because PostgREST
  // validates the token against Supabase's JWT_SECRET, not our TOKEN_SIGNING_SECRET.
  // Triple-review finding (Codex HIGH, Wave 2): user token on REST reads breaks reads.
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const isEdgeFunction = endpoint.startsWith('/functions/v1/');
    const userToken = isEdgeFunction ? await _userTokenProvider?.() : null;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'apikey': this.anonKey,
      'Authorization': `Bearer ${userToken ?? this.anonKey}`,
      ...options.headers,
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        const message = error.message || error.error || `HTTP ${response.status}`;
        if (response.status === 401 || response.status === 403) {
          // Notify subscribers (ListPage, sync) to clear stale token + re-prompt.
          // Pre-Phase-1a this dispatched a window CustomEvent; Phase 1a moved
          // the contract to IAuthProvider.notifyUnauthorized. The handler is
          // injected at boot in main.tsx so api.ts stays free of container imports.
          // The empty listName here matches today's broadcast semantics — the
          // consumer (ListPage) reads listName from its own scope.
          _unauthorizedHandler?.('');
          throw new UnauthorizedError(message);
        }
        throw new Error(message);
      }

      return response.json();
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Edge Function calls — public for Phase A components that call EFs directly
  async callFunction<T>(functionName: string, body: Record<string, unknown>): Promise<T> {
    return this.request(`/functions/v1/${functionName}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Phase A: Get list data via privacy-aware edge function
  // Returns list info + items, enforcing membership/password access control
  async getListData(slug: string, accessToken?: string | null, signal?: AbortSignal): Promise<{
    list: ListData;
    items: ItemData[];
    user_role: string | null;
  } | { error: string; list?: Partial<ListData> }> {
    const headers: HeadersInit = {};
    if (accessToken) {
      headers['X-Access-Token'] = accessToken;
    }
    return this.request(`/functions/v1/get-list-data?slug=${encodeURIComponent(slug)}`, {
      method: 'GET',
      headers,
      signal,
    });
  }

  // Phase A: My Lists (authenticated user's list memberships)
  async getMyLists(): Promise<MyListItem[]> {
    const result = await this.request<{ lists: MyListItem[] }>('/functions/v1/my-lists', {
      method: 'GET',
    });
    return result.lists;
  }

  // GDPR-endpoints (TODO.md#service-layer-gdpr-fetch, triple-review #9):
  // DataPrivacySection/UserDebugPanel lavede raw fetch mod VITE_SUPABASE_URL
  // udenom service-laget. Brugertoken vedhæftes automatisk af request()
  // via _userTokenProvider (container.auth.getUserToken, wired i main.tsx).
  async exportUserData(): Promise<Record<string, unknown>> {
    return this.request('/functions/v1/me-export', { method: 'GET' });
  }

  async deleteUserAccount(): Promise<{ success?: boolean; note?: string }> {
    return this.request('/functions/v1/me-delete', {
      method: 'POST',
      body: JSON.stringify({ confirm: 'DELETE_MY_ACCOUNT' }),
    });
  }

  // List operations
  async createList({ name, password, viewRequiresPassword, editRequiresPassword }: CreateListParams): Promise<ListData> {
    return this.callFunction('create-list', {
      name,
      password: password || null,
      view_requires_password: viewRequiresPassword ?? false,
      edit_requires_password: editRequiresPassword ?? false,
    });
  }

  // Triple-review 2026-06-11 C1: hash columns are no longer SELECT-granted to
  // client roles. The generated columns has_access_password / has_admin_password
  // (migration 20260611000000) carry the boolean flags instead.
  async getList(name: string, signal?: AbortSignal): Promise<ListData[]> {
    const nameLower = name.toLowerCase();
    const lists = await this.request<Array<Omit<ListData, 'list_access_password_hash' | 'list_admin_password_hash' | 'name_lowercase'>>>(
      `/rest/v1/lists?name_lowercase=eq.${encodeURIComponent(nameLower)}&select=id,name,slug,owner_user_id,has_access_password,has_admin_password,view_requires_password,edit_requires_password,created_at`,
      { signal }
    );
    return lists.map(list => ({
      ...list,
      name_lowercase: list.name.toLowerCase(),
      list_access_password_hash: null,
      list_admin_password_hash: null,
    }));
  }

  // Slug-based lookup (Phase A: slug URLs)
  async getListBySlug(slug: string, signal?: AbortSignal): Promise<ListData | null> {
    const lists = await this.request<Array<Omit<ListData, 'list_access_password_hash' | 'list_admin_password_hash' | 'name_lowercase'>>>(
      `/rest/v1/lists?slug=eq.${encodeURIComponent(slug)}&select=id,name,slug,owner_user_id,has_access_password,has_admin_password,view_requires_password,edit_requires_password,created_at,shopping_country,list_language`,
      { signal }
    );
    if (lists.length === 0) return null;
    const list = lists[0];
    return {
      ...list,
      name_lowercase: list.name.toLowerCase(),
      list_access_password_hash: null,
      list_admin_password_hash: null,
    };
  }

  async verifyPassword(listId: string, password: string, action: 'view' | 'edit' | 'admin' = 'view'): Promise<{ success: boolean; token?: string }> {
    return this.callFunction('verify-password', {
      list_id: listId,
      password,
      action,
    });
  }

  // Item operations (through edge function for password verification)
  async getItems(listId: string, token: string | null = null): Promise<ItemData[]> {
    const headers: HeadersInit = {};
    if (token) {
      headers['X-Access-Token'] = token;
    }

    return this.request<ItemData[]>(
      `/rest/v1/items?list_id=eq.${listId}&order=created_at.asc&select=*`,
      { headers }
    );
  }

  async addItem(listId: string, item: Partial<ItemData>, token: string | null = null): Promise<ItemData> {
    return this.callFunction('manage-items', {
      action: 'add',
      list_id: listId,
      item,
      token,
      sender_device_id: getDeviceId(),
    });
  }

  async updateItem(itemId: string, updates: Partial<ItemData>, token: string | null = null): Promise<ItemData> {
    return this.callFunction('manage-items', {
      action: 'update',
      item_id: itemId,
      updates,
      token,
      sender_device_id: getDeviceId(),
    });
  }

  async toggleItemBought(itemId: string, isBought: boolean, token: string | null = null): Promise<ItemData> {
    return this.callFunction('manage-items', {
      action: 'update',
      item_id: itemId,
      updates: { is_bought: isBought },
      token,
      sender_device_id: getDeviceId(),
    });
  }

  async deleteItem(itemId: string, token: string | null = null): Promise<{ success: boolean }> {
    return this.callFunction('manage-items', {
      action: 'delete',
      item_id: itemId,
      token,
      sender_device_id: getDeviceId(),
    });
  }

  // Check if list name exists
  async listExists(name: string): Promise<boolean> {
    const lists = await this.getList(name);
    return lists && lists.length > 0;
  }

  // List management - Access password (for view/edit)
  async updateAccessPassword(
    listId: string,
    adminPassword: string | null,
    newPassword: string | null | undefined, // undefined = keep existing, null = remove, string = set new
    viewRequiresPassword: boolean,
    editRequiresPassword: boolean
  ): Promise<{ success: boolean }> {
    const body: Record<string, unknown> = {
      action: 'update_access_password',
      list_id: listId,
      admin_password: adminPassword,
      view_requires_password: viewRequiresPassword,
      edit_requires_password: editRequiresPassword,
    };

    // Only include new_password if it's not undefined (null = remove, string = set new)
    if (newPassword !== undefined) {
      body.new_password = newPassword;
    }

    return this.callFunction('manage-list', body);
  }

  // List management - Admin password (for settings)
  async updateAdminPassword(
    listId: string,
    currentAdminPassword: string | null,
    newAdminPassword: string | null
  ): Promise<{ success: boolean }> {
    return this.callFunction('manage-list', {
      action: 'update_admin_password',
      list_id: listId,
      current_admin_password: currentAdminPassword,
      new_admin_password: newAdminPassword,
    });
  }

  async deleteList(listId: string, adminPassword: string | null): Promise<{ success: boolean }> {
    return this.callFunction('manage-list', {
      action: 'delete',
      list_id: listId,
      admin_password: adminPassword,
    });
  }

  // Check if list has a password
  async listHasPassword(listId: string): Promise<boolean> {
    const lists = await this.request<Array<{ view_requires_password: boolean; edit_requires_password: boolean }>>(
      `/rest/v1/lists?id=eq.${listId}&select=view_requires_password,edit_requires_password`
    );
    if (lists && lists.length > 0) {
      return lists[0].view_requires_password || lists[0].edit_requires_password;
    }
    return false;
  }

  // Push notification subscriptions.
  // Triple-review 2026-06-11 C4: push-subscriptions now requires view access
  // (list token for password lists, user JWT for owned lists) and unsubscribe
  // must be scoped to this device's endpoint.
  async savePushSubscription(listId: string, subscription: PushSubscriptionJSON, deviceId?: string, token?: string | null): Promise<{ success: boolean }> {
    return this.callFunction('push-subscriptions', {
      action: 'subscribe',
      list_id: listId,
      subscription,
      device_id: deviceId,
      token: token ?? null,
    });
  }

  async deletePushSubscription(listId: string, endpoint: string, token?: string | null): Promise<{ success: boolean }> {
    return this.callFunction('push-subscriptions', {
      action: 'unsubscribe',
      list_id: listId,
      endpoint,
      token: token ?? null,
    });
  }

  // Archive operations
  async archiveBoughtItems(listId: string, token: string | null = null): Promise<{ success: boolean; undo_data?: UndoData }> {
    return this.callFunction('manage-archives', {
      action: 'archive_bought',
      list_id: listId,
      token,
    });
  }

  async deleteBoughtItems(listId: string, token: string | null = null): Promise<{ success: boolean; undo_data?: UndoData }> {
    return this.callFunction('manage-archives', {
      action: 'delete_bought',
      list_id: listId,
      token,
    });
  }

  async deleteAllItems(listId: string, token: string | null = null): Promise<{ success: boolean; undo_data?: UndoData }> {
    return this.callFunction('manage-archives', {
      action: 'delete_all',
      list_id: listId,
      token,
    });
  }

  async getArchives(listId: string, token: string | null = null): Promise<{ archives: ArchiveData[] }> {
    return this.callFunction('manage-archives', {
      action: 'get_archives',
      list_id: listId,
      token,
    });
  }

  async deleteArchive(archiveId: string, listId: string, token: string | null = null): Promise<{ success: boolean; undo_data?: UndoData }> {
    return this.callFunction('manage-archives', {
      action: 'delete_archive',
      archive_id: archiveId,
      list_id: listId,
      token,
    });
  }

  async undoArchiveAction(listId: string, undoData: UndoData, token: string | null = null): Promise<{ success: boolean }> {
    return this.callFunction('manage-archives', {
      action: 'undo',
      list_id: listId,
      undo_data: undoData,
      token,
    });
  }

  // AI-powered text parsing
  async parseTextToItems(
    listId: string,
    text: string,
    token: string | null = null,
    preferredProvider: 'cloudflare' | 'openrouter' | 'premium' | 'premium-plus' = 'cloudflare'
  ): Promise<ParseTextResponse> {
    return this.callFunction('parse-items-with-llm', {
      list_id: listId,
      text,
      token,
      preferred_provider: preferredProvider,
    });
  }

  // Batch add items
  async batchAddItems(
    listId: string,
    items: BatchAddItem[],
    token: string | null = null
  ): Promise<BatchAddResponse> {
    return this.callFunction('batch-add-items', {
      list_id: listId,
      items,
      token,
      sender_device_id: getDeviceId(),
    });
  }

  // Translation feature
  async translateItems(
    listId: string,
    items: TranslationItem[],
    targetCountry: string,
    token: string | null = null
  ): Promise<TranslateResponse> {
    return this.callFunction('translate-items', {
      list_id: listId,
      items,
      target_country: targetCountry,
      token,
    });
  }

  // Update list shopping country
  async updateShoppingCountry(
    listId: string,
    shoppingCountry: string | null,
    token: string | null = null
  ): Promise<{ success: boolean }> {
    return this.callFunction('manage-list', {
      action: 'update_shopping_country',
      list_id: listId,
      shopping_country: shoppingCountry,
      token,
    });
  }

  // Update list language (source language for items)
  async updateListLanguage(
    listId: string,
    listLanguage: string,
    token: string | null = null
  ): Promise<{ success: boolean }> {
    return this.callFunction('manage-list', {
      action: 'update_list_language',
      list_id: listId,
      list_language: listLanguage,
      token,
    });
  }
}

// Export singleton instance
const api = new ApiClient();
export default api;
